import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { motion } from 'framer-motion';

// Global Styles
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', sans-serif;
    font-size: 16px;
    line-height: 1.5;
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
    color: #ffffff;
    min-height: 100vh;
    overflow: hidden;
    user-select: none;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    padding-bottom: env(safe-area-inset-bottom);
  }
`;

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3), 0 0 100px rgba(118, 75, 162, 0.2); }
  50% { box-shadow: 0 30px 80px rgba(102, 126, 234, 0.5), 0 0 150px rgba(118, 75, 162, 0.4); }
`;

// Styled Components
const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px 20px 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%);
    top: -250px;
    right: -250px;
    animation: ${float} 8s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(118, 75, 162, 0.15) 0%, transparent 70%);
    bottom: -200px;
    left: -200px;
    animation: ${float} 10s ease-in-out infinite reverse;
  }
  
  @media (max-width: 768px) {
    padding: 0 10px 60px 10px;
    padding-bottom: calc(60px + env(safe-area-inset-bottom));
    align-items: flex-start;
    justify-content: center;
  }
`;

const CardWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  height: 100vh;
  position: relative;
  z-index: 1;
  border-radius: 32px;
  padding: 2px;
  background: linear-gradient(135deg, 
    rgba(102, 126, 234, 0.4) 0%, 
    rgba(118, 75, 162, 0.4) 25%,
    rgba(236, 72, 153, 0.3) 50%,
    rgba(168, 85, 247, 0.4) 75%,
    rgba(102, 126, 234, 0.4) 100%
  );
  background-size: 200% 200%;
  animation: ${glow} 4s ease-in-out infinite, gradientShift 8s ease-in-out infinite;
  
  @keyframes gradientShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  @media (max-width: 768px) {
    height: 100vh;
    border-radius: 24px;
    padding: 2px;
    animation: none;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.3), rgba(118, 75, 162, 0.3));
    background-size: 100% 100%;
    margin-bottom: 20px;
  }
`;

const DigitalCard = styled(motion.div)`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, 
    rgba(10, 10, 26, 0.98) 0%, 
    rgba(15, 15, 35, 0.98) 25%,
    rgba(20, 20, 40, 0.98) 50%,
    rgba(15, 15, 35, 0.98) 75%,
    rgba(10, 10, 26, 0.98) 100%
  );
  backdrop-filter: blur(60px) saturate(180%);
  border-radius: 30px;
  padding: 0 2.5rem 2.5rem 2.5rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(102, 126, 234, 0.1),
      transparent
    );
    animation: ${shimmer} 3s infinite;
    pointer-events: none;
    z-index: 1;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  /* Premium custom scrollbar - 50% smaller */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    margin: 10px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
    border-radius: 10px;
    min-height: 40px;
    
    &:hover {
      background: linear-gradient(180deg, #764ba2 0%, #667eea 100%);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0 1.5rem 3rem 1.5rem;
    height: 100%;
    border-radius: 18px;
    
    &::-webkit-scrollbar {
      width: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
      min-height: 30px;
    }
  }
`;

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding-top: 0;
  }
`;

const CompanyName = styled.h1`
  font-size: 2.75rem;
  font-weight: 800;
  line-height: 1.2;
  background: linear-gradient(135deg, #667eea 0%, #a855f7 50%, #ec4899 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;
  animation: gradientText 3s ease-in-out infinite;
  
  @keyframes gradientText {
    0%, 100% { background-position: 0% center; }
    50% { background-position: 100% center; }
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-top: 1.5rem;
    animation: none;
    background: none;
    color: #667eea;
    -webkit-text-fill-color: #667eea;
  }
`;

const Tagline = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const WebsiteLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.12) 100%);
  border: 1.5px solid rgba(102, 126, 234, 0.35);
  border-radius: 50px;
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0.75rem;
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    transform: translateY(-50%);
    box-shadow: 0 0 12px rgba(102, 126, 234, 0.6);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
    border-color: rgba(102, 126, 234, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.35);
    
    &::before {
      box-shadow: 0 0 16px rgba(102, 126, 234, 0.8);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.875rem;
    gap: 0.6rem;
    
    &::before {
      width: 6px;
      height: 6px;
      left: 0.6rem;
    }
  }
`;

const CardBody = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  
  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const Section = styled.div`
  width: 100%;
  max-width: 1100px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.3;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2.5rem;
  position: relative;
  display: inline-block;
  text-align: center;
  padding-bottom: 1rem;
  letter-spacing: -0.02em;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.6);
  }
  
  @media (max-width: 768px) {
    font-size: 1.625rem;
    margin-bottom: 2rem;
    padding-bottom: 0.875rem;
    
    &::after {
      height: 3px;
      width: 60px;
    }
  }
`;

const ServicesGrid = styled.div<{ $itemCount?: number }>`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ServiceCard = styled(motion.div)`
  flex: 0 0 calc(33.333% - 1rem);
  max-width: 360px;
  min-width: 300px;
  background: linear-gradient(135deg, 
    rgba(102, 126, 234, 0.08) 0%, 
    rgba(118, 75, 162, 0.08) 100%
  );
  border: 1.5px solid rgba(102, 126, 234, 0.2);
  border-radius: 24px;
  padding: 2.5rem;
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 12px 48px rgba(0, 0, 0, 0.2),
    0 4px 12px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  backdrop-filter: blur(12px);
  
  @media (max-width: 1100px) {
    flex: 0 0 calc(50% - 0.75rem);
  }
  
  @media (max-width: 768px) {
    flex: 0 0 100%;
    max-width: 100%;
    padding: 1.5rem;
    backdrop-filter: none;
    transition: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
      rgba(102, 126, 234, 0.15) 0%,
      transparent 50%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      rgba(102, 126, 234, 0.3) 0%, 
      rgba(168, 85, 247, 0.3) 50%,
      rgba(236, 72, 153, 0.3) 100%
    );
    border-radius: 24px;
    opacity: 0;
    transition: opacity 0.5s ease;
    z-index: -1;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &:hover {
    background: linear-gradient(135deg, 
      rgba(102, 126, 234, 0.12) 0%, 
      rgba(118, 75, 162, 0.12) 100%
    );
    border-color: rgba(102, 126, 234, 0.4);
    transform: translateY(-12px) scale(1.03);
    box-shadow: 
      0 20px 60px rgba(102, 126, 234, 0.3),
      0 8px 24px rgba(118, 75, 162, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      opacity: 1;
    }
    
    @media (max-width: 768px) {
      transform: none;
      background: linear-gradient(135deg, 
        rgba(102, 126, 234, 0.05) 0%, 
        rgba(118, 75, 162, 0.05) 100%
      );
      border-color: rgba(102, 126, 234, 0.15);
      box-shadow: 
        0 10px 40px rgba(0, 0, 0, 0.15),
        0 2px 8px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }
  }
`;

const ServiceIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    rgba(102, 126, 234, 0.15) 0%, 
    rgba(168, 85, 247, 0.15) 50%,
    rgba(236, 72, 153, 0.15) 100%
  );
  border-radius: 16px;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 4px 20px rgba(102, 126, 234, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(102, 126, 234, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, 
      rgba(102, 126, 234, 0.25) 0%, 
      rgba(168, 85, 247, 0.25) 50%,
      rgba(236, 72, 153, 0.25) 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #667eea 0%, #a855f7 50%, #ec4899 100%);
    border-radius: 6px;
    transform: rotate(45deg);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  }
  
  ${ServiceCard}:hover & {
    transform: scale(1.1);
    box-shadow: 
      0 8px 32px rgba(102, 126, 234, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    border-color: rgba(102, 126, 234, 0.4);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: rotate(45deg) scale(1.1);
    }
    
    @media (max-width: 768px) {
      transform: none;
      box-shadow: 
        0 4px 20px rgba(102, 126, 234, 0.25),
        inset 0 1px 0 rgba(255, 255, 255, 0.15);
    }
  }
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    transition: none;
    
    &::after {
      width: 20px;
      height: 20px;
    }
  }
`;

const ServiceSubtitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const PriceSub = styled.div`
  font-size: 0.85rem;
  color: rgba(102, 126, 234, 0.7);
  margin-top: 0.25rem;
  text-align: center;
  width: 100%;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  li {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.75);
    padding: 0.5rem 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    text-align: center;
    position: relative;
    
    &::before {
      content: '';
      width: 6px;
      height: 6px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 50%;
      box-shadow: 0 0 8px rgba(102, 126, 234, 0.6);
      flex-shrink: 0;
    }
  }
  
  @media (max-width: 768px) {
    li {
      font-size: 0.8rem;
      padding: 0.4rem 0;
    }
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.4;
  color: #ffffff;
  margin-bottom: 0.75rem;
  text-align: center;
  width: 100%;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ServiceDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 1.25rem;
  text-align: center;
  width: 100%;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const ServicePrice = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.4;
  color: #667eea;
  text-align: center;
  width: 100%;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: center;
  width: 100%;
`;

const Tag = styled.span`
  background: linear-gradient(135deg, 
    rgba(102, 126, 234, 0.18) 0%, 
    rgba(118, 75, 162, 0.18) 100%
  );
  color: rgba(102, 126, 234, 1);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1.5px solid rgba(102, 126, 234, 0.3);
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
  
  &:hover {
    background: linear-gradient(135deg, 
      rgba(102, 126, 234, 0.28) 0%, 
      rgba(168, 85, 247, 0.28) 100%
    );
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.85rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 400px));
  gap: 2rem;
  width: 100%;
  justify-content: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const InfoCard = styled(motion.div)`
  text-align: center;
  padding: 2.5rem 2rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-radius: 24px;
  border: 1.5px solid rgba(102, 126, 234, 0.25);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  &:hover {
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 20px 60px rgba(102, 126, 234, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
    
    &::before {
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const InfoIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
  border-radius: 16px;
  border: 2px solid rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.25) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    transform: rotate(45deg);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  }
  
  ${InfoCard}:hover & {
    transform: scale(1.1);
    border-color: rgba(102, 126, 234, 0.6);
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.4);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: rotate(45deg) scale(1.1);
    }
  }
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    
    &::after {
      width: 24px;
      height: 24px;
    }
  }
`;

const InfoValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const InfoLabel = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ContactItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-radius: 16px;
  border: 1.5px solid rgba(102, 126, 234, 0.25);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.18) 0%, rgba(118, 75, 162, 0.18) 100%);
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.25);
    
    &::before {
      opacity: 1;
    }
    
    i {
      transform: scale(1.1);
      color: #764ba2;
    }
  }
  
  i {
    font-size: 1.8rem;
    color: #667eea;
    min-width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(102, 126, 234, 0.12);
    border-radius: 12px;
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;
    border: 1.5px solid rgba(102, 126, 234, 0.25);
  }
  
  div {
    flex: 1;
    text-align: left;
    position: relative;
    z-index: 1;
    
    strong {
      display: block;
      color: #ffffff;
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 0.4rem;
      letter-spacing: 0.3px;
    }
    
    span, a {
      display: block;
      color: rgba(255, 255, 255, 0.85);
      font-size: 0.95rem;
      text-decoration: none;
      font-weight: 400;
      transition: color 0.3s ease;
      
      &:hover {
        color: #667eea;
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 1rem;
    
    i {
      font-size: 1.5rem;
      min-width: 45px;
      height: 45px;
      border-radius: 10px;
    }
    
    div {
      strong {
        font-size: 0.9rem;
      }
      
      span, a {
        font-size: 0.875rem;
      }
    }
  }
`;

const ContactSection = styled.div`
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContactButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 2rem 0;
  flex-wrap: wrap;
`;

const ContactButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.5);
  }
  
  &:nth-child(2) {
    background: transparent;
    border: 2px solid #667eea;
    color: #667eea;
    
    &:hover {
      background: rgba(102, 126, 234, 0.1);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const FinalCTA = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding: 2rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 20px;
  border: 1px solid rgba(102, 126, 234, 0.2);
  margin-bottom: 2rem;
  
  h3 {
    font-size: 1.8rem;
    font-weight: 700;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    margin-top: 0.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 3rem;
    
    h3 {
      font-size: 1.3rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

// Types
interface ServiceWithFeatures {
  title: string;
  subtitle?: string;
  description: string;
  price: string;
  priceSub: string;
  features: string[];
}

interface ServiceWithTags {
  title: string;
  description: string;
  tags: string[];
}

// Services Data - Split into Core Services and Expertise Areas
const coreServices: ServiceWithFeatures[] = [
    {
      title: 'SaaS Development',
      description: 'Complete subscription-based software solutions with managed infrastructure and ongoing enhancements',
      price: 'Starting from ₹10,000/month',
      priceSub: '($120+/month)',
      features: [
        'Full-stack development',
        'Cloud hosting & maintenance', 
        'Feature updates & support'
      ]
    },
  {
    title: 'Custom Solutions',
    subtitle: 'Product Development',
    description: 'Bespoke software tailored to your needs, from intelligent automation to cutting-edge immersive tech',
    price: 'Starting from ₹332,000',
    priceSub: '($4,000+)',
    features: [
      'Complexity-based pricing',
      'AI/ML integration',
      'IoT & AR/VR development'
    ]
  }
];

const expertiseAreas: ServiceWithTags[] = [
  {
    title: 'Web & App Development',
    description: 'Responsive websites, web applications, and cross-platform mobile apps',
    tags: ['Responsive Design', 'Progressive Web Apps', 'Native Apps', 'Cross-platform']
  },
  {
    title: 'AI/ML & AR/VR',
    description: 'Machine learning, computer vision, and immersive experiences',
    tags: ['Machine Learning', 'Custom ML Models', 'Chatbots', 'Computer Vision', 'AR/VR']
  },
  {
    title: 'Real Estate Solutions',
    description: 'Property management, virtual tours, and market analysis',
    tags: ['Property Management', 'Properties', 'Virtual Tours', 'Analytics']
  },
  {
    title: 'IoT Automations',
    description: 'Smart devices, sensor networks, and real-time monitoring',
    tags: ['Smart Devices', 'Sensor Networks', 'Automation Systems']
  },
  {
    title: 'Open to Any Tech Stack',
    description: 'Flexible solutions with any technology',
    tags: ['MEAN/MERN', 'Python', 'Java', 'AWS/Docker', '...etc']
  }
];

const companyInfo = [
  { 
    label: 'Guaranteed On-Time Delivery',
    value: '100%'
  },
  { 
    label: '24/7 Support',
    value: 'Always Available'
  }
];

function App() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // No animations on mobile for performance
  const getAnimationProps = (props: any) => {
    if (isMobile) {
      // Return empty object to render without animations
      return { initial: false, animate: false, whileHover: undefined, whileTap: undefined };
    }
    return props;
  };

  return (
    <AppContainer>
      <GlobalStyle />
      
      <CardWrapper>
        <DigitalCard
          {...getAnimationProps({
            initial: { opacity: 0, scale: 0.9, y: 50 },
            animate: { opacity: 1, scale: 1, y: 0 },
            transition: { duration: 0.8, ease: "easeOut" }
          })}
        >
          <CardHeader>
            <motion.div
              {...getAnimationProps({
                initial: { opacity: 0, y: -20 },
                animate: { opacity: 1, y: 0 },
                transition: { delay: 0.3, duration: 0.6 }
              })}
            >
              <CompanyName>HayCod Solutions</CompanyName>
              <Tagline>Digital Innovation Partner</Tagline>
              <WebsiteLink
                href="https://haycod.com"
                target="_blank"
                rel="noopener noreferrer"
                {...getAnimationProps({
                  initial: { opacity: 0, scale: 0.8 },
                  animate: { opacity: 1, scale: 1 },
                  transition: { delay: 0.5, duration: 0.4 },
                  whileHover: { scale: 1.05 },
                  whileTap: { scale: 0.95 }
                })}
              >
                Visit Our Website
              </WebsiteLink>
            </motion.div>
          </CardHeader>

          <CardBody>
            {/* Core Services Section */}
            <Section>
              <SectionTitle>Core Services</SectionTitle>
              <ServicesGrid $itemCount={2}>
                {coreServices.map((service, index) => (
                  <ServiceCard
                    key={index}
                    {...getAnimationProps({
                      initial: { opacity: 0, y: 20 },
                      animate: { opacity: 1, y: 0 },
                      transition: { delay: 0.5 + index * 0.1, duration: 0.5 },
                      whileHover: { scale: 1.02 }
                    })}
                  >
                    <ServiceIcon></ServiceIcon>
                    <ServiceTitle>{service.title}</ServiceTitle>
                    {service.subtitle && <ServiceSubtitle>{service.subtitle}</ServiceSubtitle>}
                    <ServiceDescription>{service.description}</ServiceDescription>
                    {service.price && (
                      <>
                        <ServicePrice>{service.price}</ServicePrice>
                        {service.priceSub && <PriceSub>{service.priceSub}</PriceSub>}
                      </>
                    )}
                    <FeatureList>
                      {service.features.map((feature: string, idx: number) => (
                        <li key={idx}>
                          {feature}
                        </li>
                      ))}
                    </FeatureList>
                  </ServiceCard>
                ))}
              </ServicesGrid>
            </Section>

            {/* Expertise Areas Section */}
            <Section>
              <SectionTitle>Expertise Areas</SectionTitle>
              <ServicesGrid $itemCount={5}>
                {expertiseAreas.map((service, index) => (
                <ServiceCard
                  key={index}
                  {...getAnimationProps({
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    transition: { delay: 0.5 + index * 0.1, duration: 0.5 },
                    whileHover: { scale: 1.02 }
                  })}
                >
                  <ServiceIcon></ServiceIcon>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                  <TagsContainer>
                    {service.tags.map((tag: string, idx: number) => (
                      <Tag key={idx}>{tag}</Tag>
                    ))}
                  </TagsContainer>
                </ServiceCard>
              ))}
              </ServicesGrid>
            </Section>

            {/* Company Information Section */}
          <Section>
            <SectionTitle>Why Choose Us</SectionTitle>
            <InfoGrid>
              {companyInfo.map((info, index) => (
                <InfoCard
                  key={index}
                  {...getAnimationProps({
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    transition: { delay: 0.8 + index * 0.1, duration: 0.5 },
                    whileHover: { scale: 1.05 }
                  })}
                >
                  <InfoIcon></InfoIcon>
                  <InfoValue>{info.value}</InfoValue>
                  <InfoLabel>{info.label}</InfoLabel>
                </InfoCard>
              ))}
            </InfoGrid>
          </Section>

          {/* Contact Information Section */}
          <Section>
            <SectionTitle>Contact Information</SectionTitle>
            <ContactInfo>
              <ContactItem
                as="a"
                href="mailto:contact-us@haycod.com"
                {...getAnimationProps({
                  initial: { opacity: 0, x: -20 },
                  animate: { opacity: 1, x: 0 },
                  transition: { delay: 1.0, duration: 0.5 },
                  whileHover: { scale: 1.03 },
                  whileTap: { scale: 0.98 }
                })}
              >
                <i className="fas fa-envelope"></i>
                <div>
                  <strong>Email</strong>
                  <span>contact-us@haycod.com</span>
                </div>
              </ContactItem>

              <ContactItem
                as="a"
                href="https://wa.me/918660530747"
                target="_blank"
                rel="noopener noreferrer"
                {...getAnimationProps({
                  initial: { opacity: 0, x: 20 },
                  animate: { opacity: 1, x: 0 },
                  transition: { delay: 1.1, duration: 0.5 },
                  whileHover: { scale: 1.03 },
                  whileTap: { scale: 0.98 }
                })}
              >
                <i className="fab fa-whatsapp"></i>
                <div>
                  <strong>WhatsApp</strong>
                  <span>+91 8660530747</span>
                </div>
              </ContactItem>

              <ContactItem
                as="a"
                href="https://www.linkedin.com/company/haycod-solutions/"
                target="_blank"
                rel="noopener noreferrer"
                {...getAnimationProps({
                  initial: { opacity: 0, x: -20 },
                  animate: { opacity: 1, x: 0 },
                  transition: { delay: 1.2, duration: 0.5 },
                  whileHover: { scale: 1.03 },
                  whileTap: { scale: 0.98 }
                })}
              >
                <i className="fab fa-linkedin"></i>
                <div>
                  <strong>LinkedIn</strong>
                  <span>HayCod Solutions</span>
                </div>
              </ContactItem>

              <ContactItem
                as="a"
                href="https://www.instagram.com/haycodsolutions/"
                target="_blank"
                rel="noopener noreferrer"
                {...getAnimationProps({
                  initial: { opacity: 0, x: 20 },
                  animate: { opacity: 1, x: 0 },
                  transition: { delay: 1.3, duration: 0.5 },
                  whileHover: { scale: 1.03 },
                  whileTap: { scale: 0.98 }
                })}
              >
                <i className="fab fa-instagram"></i>
                <div>
                  <strong>Instagram</strong>
                  <span>@haycodsolutions</span>
                </div>
              </ContactItem>
            </ContactInfo>
          </Section>

          {/* Final CTA Section */}
          <Section>
            <ContactSection>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
              >
                <FinalCTA>
                  <h3>Let's Create Something Amazing!</h3>
                  <p>Innovation starts with a conversation</p>
                </FinalCTA>
              </motion.div>
            </ContactSection>
          </Section>
          </CardBody>
        </DigitalCard>
      </CardWrapper>
    </AppContainer>
  );
}

export default App;
